import { Mention } from '@syncfusion/ej2-dropdowns';
import { isNullOrUndefined as isNOU } from '@syncfusion/ej2-base';
import { defaultSlashMenuDataModel, injectibleSlashMenuDataModel } from '../../models/slash-menu-settings';
import { NodeSelection } from '../../selection/selection';
import { slashMenuCommandsKey } from '../models/default-locale';
import * as events from '../base/constant';
import { DialogType } from '../../common/enum';
export class SlashMenu {
    constructor(options, serviceLocator) {
        this.parent = options;
        this.currentDocument = this.parent.element.ownerDocument;
        this.L10n = serviceLocator.getService('rteLocale');
        this.savedSelection = new NodeSelection(this.parent.inputElement);
        this.defaultItems = defaultSlashMenuDataModel;
        this.injectibleItems = injectibleSlashMenuDataModel;
        this.parent.on(events.modelChanged, this.onPropertyChanged, this);
        this.parent.on(events.destroy, this.removeEventListener, this);
        this.parent.on(events.initialEnd, this.render, this);
    }
    removeEventListener() {
        this.parent.off(events.modelChanged, this.onPropertyChanged);
        this.parent.off(events.destroy, this.removeEventListener);
    }
    onPropertyChanged(e) {
        for (const prop of Object.keys(e.newProp)) {
            if (prop === 'slashMenuSettings') {
                switch (Object.keys(e.newProp.slashMenuSettings)[0]) {
                    case 'enable':
                        if (!e.newProp.slashMenuSettings.enable) {
                            this.removeEventListener();
                        }
                        else {
                            this.render();
                        }
                        break;
                    case 'items':
                        this.mention.dataSource = this.getItems();
                        break;
                    case 'popupHeight':
                        this.mention.popupHeight = e.newProp.slashMenuSettings.popupHeight;
                        break;
                    case 'popupWidth':
                        this.mention.popupWidth = e.newProp.slashMenuSettings.popupWidth;
                        break;
                }
            }
        }
    }
    getModuleName() {
        return 'slashMenu';
    }
    destroy() {
        if (this.mention && !this.mention.isDestroyed) {
            this.mention.destroy();
            this.defaultItems = [];
            this.injectibleItems = [];
        }
    }
    generateMentionModel() {
        const dataSource = this.getItems();
        const model = {
            dataSource: dataSource,
            cssClass: 'e-slash-menu e-rte-elements' + this.parent.getCssClass(),
            fields: { text: 'text', groupBy: 'type', iconCss: 'iconCss', value: 'description' },
            mentionChar: '/',
            target: this.parent.inputElement,
            popupHeight: this.parent.slashMenuSettings.popupHeight,
            popupWidth: this.parent.slashMenuSettings.popupWidth,
            allowSpaces: true,
            itemTemplate: '${if(iconCss && description)}' +
                '<div class="e-rte-slash-menu-item-content-description">' +
                '<div class="e-slash-menu-icon"><div class="${iconCss}"></div></div> ' +
                '<div class="e-rte-slash-menu-item-text-column">' +
                '<span class="e-rte-slash-menu-item-text">${text}</span>' +
                '${if(description)}' +
                '<span class="e-rte-slash-menu-item-description">${description}</span>' +
                '${/if}' +
                '</div>' +
                '</div>' +
                '${else}' +
                '${if(iconCss && text)}' +
                '<div class="e-rte-slash-menu-item-content-text">' +
                '<div class="e-slash-menu-icon"><div class="${iconCss}"></div></div> ' +
                '<span class="e-rte-slash-menu-item-icon-text">${text}</span>' +
                '</div>' +
                '${/if}' +
                '${/if}',
            beforeOpen: () => {
                // Add notification to prevent zero-width space removal in html-editor keyUp event
                this.parent.notify(events.slashMenuOpening, {});
                this.savedSelection = this.savedSelection.save(this.savedSelection.getRange(this.currentDocument), this.currentDocument);
            },
            filtering: () => {
                this.savedSelection = this.savedSelection.save(this.savedSelection.getRange(this.currentDocument), this.currentDocument);
            },
            select: this.handleSelect.bind(this)
        };
        return model;
    }
    handleSelect(args) {
        args.cancel = true;
        this.parent.focusIn();
        this.savedSelection.restore();
        const item = args.itemData;
        const selectEventArgs = {
            isInteracted: args.isInteracted,
            item: args.item,
            itemData: args.itemData,
            originalEvent: args.e,
            cancel: false
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (args.itemData.isCustomItem) {
            this.parent.formatter.beforeSlashMenuApply();
        }
        this.parent.trigger('slashMenuItemSelect', selectEventArgs, (selectArgs) => {
            if (selectArgs.cancel) {
                return;
            }
            else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (!selectArgs.itemData.isCustomItem) {
                    this.parent.formatter.beforeSlashMenuApply();
                    const itemModel = item;
                    switch (itemModel.command) {
                        case 'OrderedList':
                            this.parent.executeCommand('insertOrderedList');
                            break;
                        case 'UnorderedList':
                            this.parent.executeCommand('insertUnorderedList');
                            break;
                        case 'Audio':
                        case 'Video':
                        case 'Image':
                        case 'Table':
                        case 'Link':
                            this.mention.hidePopup();
                            setTimeout(() => {
                                if (itemModel.subCommand === DialogType.InsertLink) {
                                    this.parent.notify(events.showLinkDialog, {});
                                }
                                else if (itemModel.subCommand === DialogType.InsertImage) {
                                    this.parent.notify(events.showImageDialog, selectEventArgs);
                                }
                                else if (itemModel.subCommand === DialogType.InsertAudio) {
                                    this.parent.notify(events.showAudioDialog, selectEventArgs);
                                }
                                else if (itemModel.subCommand === DialogType.InsertVideo) {
                                    this.parent.notify(events.showVideoDialog, selectEventArgs);
                                }
                                else if (itemModel.subCommand === DialogType.InsertTable) {
                                    this.parent.notify(events.showTableDialog, {});
                                }
                            }, 100);
                            break;
                        case 'Emojipicker':
                            this.mention.hidePopup();
                            setTimeout(() => {
                                this.parent.showEmojiPicker();
                            }, 100);
                            break;
                        case 'CodeBlock':
                            if (!isNOU(this.parent.codeBlockModule)) {
                                let defaultItem;
                                if (this.parent.codeBlockSettings.languages && this.parent.codeBlockSettings.languages.length > 0) {
                                    const filteredItems = !isNOU(this.parent.codeBlockSettings.defaultLanguage) ?
                                        this.parent.codeBlockSettings.languages.filter((item) => item.language === this.parent.codeBlockSettings.defaultLanguage) : [];
                                    defaultItem = filteredItems.length > 0 ? filteredItems[0] : this.parent.codeBlockSettings.languages[0];
                                    this.parent.executeCommand('insertCodeBlock', defaultItem);
                                }
                            }
                            break;
                        default:
                            this.parent.executeCommand('formatBlock', itemModel.subCommand);
                            break;
                    }
                    if (selectArgs.originalEvent && selectArgs.originalEvent.type === 'keydown') {
                        this.closeOpenPopup();
                    }
                    this.parent.notify(events.toolbarRefresh, {});
                }
                else {
                    this.closeOpenPopup();
                }
            }
        });
    }
    closeOpenPopup() {
        if (this.parent.inputElement.classList.contains('e-mention')) {
            const slashMenuPopup = this.parent.inputElement.ownerDocument.getElementById(this.parent.inputElement.id + '_slash_menu_popup');
            const isSlashMenuPopupOpen = slashMenuPopup && slashMenuPopup.classList.contains('e-popup-open');
            if (isSlashMenuPopupOpen) {
                this.mention.hidePopup();
            }
        }
    }
    getItems() {
        const items = this.parent.slashMenuSettings.items;
        const dataSource = [];
        for (let i = 0; i < items.length; i++) {
            // Predefined slash commands processing
            if (typeof items[i] === 'string') {
                const commnadName = items[i];
                let model = this.defaultItems.filter((item) => item.command === commnadName)[0];
                if (isNOU(model)) {
                    model = this.injectibleItems.filter((item) => item.module.toLowerCase().replace(' ', '') === commnadName.toLowerCase().replace(' ', ''))[0];
                }
                const localeVariable = slashMenuCommandsKey.get(commnadName);
                dataSource.push({
                    text: this.L10n.getConstant(localeVariable.text),
                    command: model.command,
                    subCommand: model.subCommand,
                    type: model.type,
                    module: model.module,
                    iconCss: model.iconCss,
                    description: this.L10n.getConstant(localeVariable.description)
                });
            }
            else { // Custom slash commands processing
                dataSource.push({
                    text: items[i].text,
                    command: items[i].command,
                    type: items[i].type,
                    iconCss: items[i].iconCss,
                    description: items[i].description,
                    isCustomItem: true
                });
            }
        }
        return dataSource;
    }
    render() {
        if (this.parent.editorMode === 'HTML' && this.parent.slashMenuSettings.enable) {
            const options = this.generateMentionModel();
            this.mention = new Mention(options);
            this.mention.isAngular = this.parent.isModalDialog;
            this.mention.appendTo(this.parent.rootContainer.appendChild(this.parent.createElement('div', { className: 'e-rte-slash-menu', id: this.parent.getID() + '_slash_menu' })));
        }
    }
}
